import { useCallback, useMemo, useState } from "react";
import { PageHeader } from "~/components/PageHeader";
import {
    ApiQuery,
    SortDirections,
    FilterMetadata,
    QueryResponse,
} from "~/modules/filters/types";
import { Asset, AssetColumns } from "~/modules/assets/types";
import { DEFAULT_ASSET_LIST_QUERY } from "~/modules/assets/consts";
import { FilterFormContextProvider } from "~/modules/filters/components/FilterFormContext";
import { getScreenerAssets } from "~/modules/asset_screener/lib/getScreenerAssets";
import { AssetResultRow } from "~/modules/asset_screener/components/AssetResultRow";
import { useUser } from "~/modules/user/hooks/useUser";
import { PageSection } from "~/modules/asset_screener/components/PageSection";
import { AssetScreenerSortInput } from "~/modules/asset_screener/components/AssetScreenerSortInput";
import { AssetScreenerFilters } from "~/modules/asset_screener/components/AssetScreenerFilters";
import { getTopLevelFilters } from "~/modules/asset_screener/components/AssetScreenerFilters/functions";
import { AssetScreenerList } from "~/modules/asset_screener/components/AssetScreenerList";
import { AssetScreenerListProvider } from "~/modules/asset_screener/components/AssetScreenerListProvider";
import { AssetScreenerStats } from "~/modules/asset_screener/api/queryAssetScreenerStats";
import {
    DATA_ROOM_SORT_OPTION_VALUE,
    NON_KYC_FILTER_METADATA,
} from "~/modules/asset_screener/consts";
import { normalizeFilterOperators } from "~/modules/asset_screener/lib/normalizeFilterOperators";

// // // //

interface Props {
    initialState: QueryResponse<Asset>;
    dealroomAssets: Asset[];
    filterMetadata: FilterMetadata[];
    stats: AssetScreenerStats;
}

/**
 *
 * DealRoomAssets
 * Renders deal room assets as expandable AssetResultRows, tracking which rows are expanded.
 */
function DealRoomAssets(props: { assets: Asset[]; isLoggedIn: boolean }) {
    const { assets, isLoggedIn } = props;

    const [expandedAssetIds, setExpandedAssetIds] = useState<Set<number>>(
        new Set(),
    );

    const screenerAssets = useMemo(() => getScreenerAssets(assets), [assets]);

    return (
        <>
            {screenerAssets.map((asset, index) => (
                <AssetResultRow
                    key={asset.id}
                    asset={asset}
                    isLoggedIn={isLoggedIn}
                    isExpanded={expandedAssetIds.has(asset.id)}
                    hasTopBorder={index === 0}
                    isFirstRow={index === 0}
                    isLastRow={index === screenerAssets.length - 1}
                    onToggle={() => {
                        setExpandedAssetIds((prev) => {
                            const newSet = new Set(prev);
                            if (newSet.has(asset.id)) {
                                newSet.delete(asset.id);
                            } else {
                                newSet.add(asset.id);
                            }
                            return newSet;
                        });
                    }}
                />
            ))}
        </>
    );
}

// // // //

/**
 * AssetScreenerPageLayout
 * Renders the asset screener page layout, including the filters,
 * sort, and list of assets, and the deal room assets.
 */
export function AssetScreenerPageLayout(props: Props) {
    const { initialState, filterMetadata, stats } = props;

    // Whether the user is logged in
    const { isLoggedIn } = useUser();

    // API query for the asset screener list
    const [apiQuery, setApiQuery] = useState<ApiQuery>({
        ...DEFAULT_ASSET_LIST_QUERY,
        sort: {
            field: AssetColumns.bridgedTokenValueDollar,
            direction: SortDirections.desc,
        },
        pagination: {
            page: 1,
            perPage: 50,
        },
    });

    // Selected sort dropdown option - defaults to "Data Room Available", which
    // matches the default sort above (see DATA_ROOM_SORT_OPTION_VALUE)
    const [selectedSortOptionValue, setSelectedSortOptionValue] = useState<
        string | null
    >(DATA_ROOM_SORT_OPTION_VALUE);

    // Whether to show the deal room assets - only with the "Data Room
    // Available" sort and no filters applied (the default query has none)
    const hasActiveFilters = getTopLevelFilters(apiQuery).length > 0;
    const showDealRoomAssets =
        selectedSortOptionValue === DATA_ROOM_SORT_OPTION_VALUE &&
        !hasActiveFilters;

    // Page-local filter fields (e.g. Non KYC Only) on top of the API's metadata
    const formFilterMetadata = useMemo(
        () => [...filterMetadata, NON_KYC_FILTER_METADATA],
        [filterMetadata],
    );

    // Handles changes to the filters
    const handleFiltersChange = useCallback(
        (updatedQuery: ApiQuery) => {
            // Reset to the first page whenever a filter changes
            setApiQuery(
                normalizeFilterOperators(
                    {
                        ...updatedQuery,
                        pagination: { ...updatedQuery.pagination, page: 1 },
                    },
                    formFilterMetadata,
                ),
            );
        },
        [formFilterMetadata],
    );

    return (
        <>
            <div className="mx-auto w-full max-w-6xl">
                <PageHeader
                    title="Asset Screener"
                    subtitle="Screen tokenized assets by yield, redemption terms, asset class, platform, and network. Returns are denominated in USD."
                />
            </div>

            <div className="mx-auto w-full max-w-6xl pt-3 space-y-4">
                <FilterFormContextProvider
                    filterMetadata={formFilterMetadata}
                    query={apiQuery}
                    onChange={handleFiltersChange}
                >
                    <AssetScreenerFilters
                        apiQuery={apiQuery}
                        onChangeQuery={handleFiltersChange}
                        apy30DayValues={stats.apy30DayValues}
                        assetClassCounts={stats.assetClassCounts}
                        networkCounts={stats.networkCounts}
                        redemptionFrequencyCounts={
                            stats.redemptionFrequencyCounts
                        }
                    />

                    <AssetScreenerListProvider
                        apiQuery={apiQuery}
                        onChangeQuery={setApiQuery}
                        initialState={initialState}
                    >
                        {({
                            screenerAssets,
                            isFetching,
                            hasNextPage,
                            loadNextPage,
                            pagination,
                        }) => (
                            <div>
                                <PageSection
                                    title="Assets"
                                    hasDataRoomBadge={showDealRoomAssets}
                                    count={
                                        showDealRoomAssets
                                            ? props.dealroomAssets.length
                                            : (pagination?.resultCount ?? 0)
                                    }
                                >
                                    <AssetScreenerSortInput
                                        showLabel={false}
                                        selectedOptionValue={
                                            selectedSortOptionValue
                                        }
                                        onSelectedOptionValueChange={
                                            setSelectedSortOptionValue
                                        }
                                    />
                                </PageSection>

                                {showDealRoomAssets && (
                                    <>
                                        <DealRoomAssets
                                            assets={props.dealroomAssets}
                                            isLoggedIn={isLoggedIn}
                                        />

                                        <PageSection
                                            title="All other assets"
                                            className="pt-8"
                                            hasDataRoomBadge={false}
                                            count={
                                                pagination
                                                    ? pagination.resultCount
                                                    : initialState.pagination
                                                          .resultCount
                                            }
                                        />
                                    </>
                                )}

                                <AssetScreenerList
                                    assets={screenerAssets}
                                    fetching={isFetching}
                                    hasNextPage={hasNextPage}
                                    loadNextPage={loadNextPage}
                                    isLoggedIn={isLoggedIn}
                                />
                            </div>
                        )}
                    </AssetScreenerListProvider>
                </FilterFormContextProvider>
            </div>
        </>
    );
}
